/**
 * Adjusting a style's stock as a grid of counts (FB-1116), as pure rules.
 *
 * Each box holds what is on the shelf; the change is the difference from what
 * the books say. One cause covers the lot. *Recounted* goes either way, as a
 * count does; the others keep the direction their cause fixes — found adds,
 * damaged, missing and returned take away — so a grid cannot write "+2
 * damaged" any more than the single form could. The server holds the same
 * rule (`adjustToCounts`); this is the screen saying it before the press.
 */

export type CountReason = 'count_correction' | 'damage' | 'shrink' | 'found' | 'vendor_return';

export const COUNT_REASONS: readonly {
  readonly value: CountReason;
  readonly label: string;
  readonly hint: string;
  /** +1 adds, -1 takes away, 0 either. */
  readonly direction: 1 | -1 | 0;
}[] = [
  { value: 'count_correction', label: 'Recounted', hint: 'the shelf says otherwise', direction: 0 },
  { value: 'damage', label: 'Damaged', hint: 'torn, broken, unsellable', direction: -1 },
  { value: 'shrink', label: 'Missing', hint: 'not on the shelf', direction: -1 },
  { value: 'found', label: 'Found', hint: 'turned up — adds to stock', direction: 1 },
  { value: 'vendor_return', label: 'Returned to vendor', hint: 'sent back', direction: -1 },
];

export interface CountedVariant {
  readonly id: string;
  readonly sku: string;
  readonly onHand: number;
}

export interface CountChange {
  readonly variantId: string;
  readonly sku: string;
  readonly from: number;
  readonly to: number;
}

/** A typed count, or null when the box does not hold a whole number of units. */
export function parseCount(typed: string | undefined): number | null {
  const trimmed = (typed ?? '').trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** The sizes whose box differs from the books, in the order given. */
export function countChanges(
  variants: readonly CountedVariant[],
  typed: Readonly<Record<string, string>>,
): CountChange[] {
  return variants.flatMap((variant) => {
    const to = parseCount(typed[variant.id] ?? String(variant.onHand));
    return to === null || to === variant.onHand
      ? []
      : [{ variantId: variant.id, sku: variant.sku, from: variant.onHand, to }];
  });
}

/** Why it cannot be saved yet, in words, or undefined when it can. */
export function whyNot(input: {
  readonly variants: readonly CountedVariant[];
  readonly typed: Readonly<Record<string, string>>;
  readonly reason: CountReason | '';
  readonly note: string;
}): string | undefined {
  const unreadable = input.variants.find(
    (variant) =>
      input.typed[variant.id] !== undefined && parseCount(input.typed[variant.id]) === null,
  );
  if (unreadable !== undefined) return `${unreadable.sku} needs a whole number, zero or more`;
  const changes = countChanges(input.variants, input.typed);
  if (changes.length === 0) return 'Change a count first';
  if (input.reason === '') return 'Say what happened';
  const cause = COUNT_REASONS.find((entry) => entry.value === input.reason)!;
  if (cause.direction !== 0) {
    const wrong = changes.find((change) => Math.sign(change.to - change.from) !== cause.direction);
    if (wrong !== undefined) {
      return `${cause.label} only ${cause.direction > 0 ? 'adds' : 'takes away'} — ${wrong.sku} goes from ${wrong.from} to ${wrong.to}. Choose Recounted`;
    }
  }
  if (input.note.trim() === '') return 'Explain it — this is the only record';
  return undefined;
}
